import { FormEvent, RefObject, useEffect, useRef, useState } from 'react';
import { ContactApiError, ContactChallenge, LeadDraft, createContactChallenge, deriveContactKind, submitContact } from './api';

type Field = keyof LeadDraft | 'consent';
type FieldErrors = Partial<Record<Field, string>>;
type Status = 'initial' | 'submitting' | 'success' | 'error' | 'rate-limited' | 'offline' | 'stale-consent';

const emptyDraft: LeadDraft = { name: '', contact: '', message: '' };
const controlCharacters = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/u;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
const phonePattern = /^[+0-9() .-]{7,32}$/u;

export function validateDraft(draft: LeadDraft, consent: boolean): FieldErrors {
  const errors: FieldErrors = {};
  const name = draft.name.trim();
  const contact = draft.contact.trim();
  const message = draft.message.trim();
  if (!name) errors.name = 'Укажите имя.';
  else if (name.length > 100 || controlCharacters.test(name)) errors.name = 'Имя должно содержать не более 100 символов без управляющих знаков.';
  if (!contact) errors.contact = 'Введите email или телефон.';
  else if (deriveContactKind(contact) === 'email' && (!emailPattern.test(contact) || contact.length > 254)) errors.contact = 'Укажите корректный email.';
  else if (deriveContactKind(contact) === 'phone' && !phonePattern.test(contact)) errors.contact = 'Укажите корректный телефон: от 7 до 32 цифр и допустимых знаков.';
  if (!message) errors.message = 'Напишите, с чем помочь.';
  else if (message.length < 10) errors.message = 'Сообщение должно содержать не менее 10 символов.';
  else if (message.length > 4000 || controlCharacters.test(message)) errors.message = 'Сообщение должно содержать не более 4000 символов без управляющих знаков.';
  if (!consent) errors.consent = 'Подтвердите отдельное согласие на обработку персональных данных.';
  return errors;
}

const fieldOrder: Field[] = ['name', 'contact', 'message', 'consent'];
const fieldLabels: Record<Field, string> = { name: 'Имя', contact: 'Email или телефон', message: 'Сообщение', consent: 'Согласие на обработку персональных данных' };

export const contactSuccessCopy = {
  eyebrow: 'Обращение принято',
  title: 'Обращение принято в обработку',
  responseTime: 'Мы ответим в течение одного рабочего дня.',
} as const;

export function ContactSuccess({ onReset, panelRef }: { onReset?: () => void; panelRef?: RefObject<HTMLElement | null> }) {
  return (
    <section ref={panelRef} className="form-panel success-panel" aria-labelledby="success-title" tabIndex={-1}>
      <p className="eyebrow">{contactSuccessCopy.eyebrow}</p>
      <h2 id="success-title">{contactSuccessCopy.title}</h2>
      <p>{contactSuccessCopy.responseTime}</p>
      <p>Если вопрос срочный или ответ не пришел, напишите на <a href="mailto:info@sparksoft.ru">info@sparksoft.ru</a>.</p>
      <button className="button secondary" type="button" onClick={onReset}>Отправить еще одно</button>
    </section>
  );
}

function uuid(): string {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  throw new Error('Secure UUID generation is unavailable');
}

export function ContactForm({ idPrefix = 'field', initialFocusRef, showHeading = true, warmChallengeOnMount = false }: { idPrefix?: string; initialFocusRef?: RefObject<HTMLInputElement | null>; showHeading?: boolean; warmChallengeOnMount?: boolean }) {
  const [draft, setDraft] = useState<LeadDraft>(emptyDraft);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [status, setStatus] = useState<Status>('initial');
  const [statusMessage, setStatusMessage] = useState('');
  const idempotencyKey = useRef<string | null>(null);
  const attemptedPayload = useRef('');
  const challenge = useRef<ContactChallenge | null>(null);
  const challengeRequest = useRef<Promise<ContactChallenge> | null>(null);
  const errorSummary = useRef<HTMLDivElement>(null);
  const statusPanel = useRef<HTMLDivElement>(null);
  const successPanel = useRef<HTMLElement>(null);
  const controlId = (field: Field) => field === 'consent' ? (idPrefix === 'field' ? 'consent' : `${idPrefix}-consent`) : `${idPrefix}-${field}`;
  // The error summary targets the consent field wrapper, while its label must
  // always target the checkbox. Keep these identifiers separate per form
  // instance so the prerendered canonical and dialog forms remain valid DOM.
  const fieldTargetId = (field: Field) => field === 'consent' ? `${idPrefix}-consent-field` : controlId(field);
  const errorId = (field: Field) => `${idPrefix}-error-${field}`;
  const hintId = (field: 'contact' | 'message' | 'consent') => `${idPrefix}-${field}-hint`;

  const focusField = (field: Field) => {
    const target = document.getElementById(fieldTargetId(field));
    target?.focus();
  };

  const warmChallenge = async (): Promise<ContactChallenge> => {
    if (challenge.current && Date.parse(challenge.current.expiresAt) > Date.now() + 1000) return challenge.current;
    if (!challengeRequest.current) {
      challengeRequest.current = createContactChallenge().then((value) => {
        challenge.current = value;
        return value;
      }).finally(() => { challengeRequest.current = null; });
    }
    return challengeRequest.current;
  };

  useEffect(() => {
    if (!warmChallengeOnMount) return;
    void warmChallenge().catch(() => undefined);
  }, [warmChallengeOnMount]);

  useEffect(() => {
    if (status === 'success') requestAnimationFrame(() => successPanel.current?.focus());
    if (['error', 'rate-limited', 'offline', 'stale-consent'].includes(status)) requestAnimationFrame(() => statusPanel.current?.focus());
  }, [status]);

  const validateField = (field: Field, nextDraft = draft, nextConsent = consent) => {
    const nextErrors = validateDraft(nextDraft, nextConsent);
    setErrors((current) => ({ ...current, [field]: nextErrors[field] }));
  };

  const editDraft = (field: keyof LeadDraft, value: string) => {
    const next = { ...draft, [field]: value };
    if (attemptedPayload.current && attemptedPayload.current !== JSON.stringify({ ...next, consent })) {
      idempotencyKey.current = null;
      attemptedPayload.current = '';
    }
    setDraft(next);
    if (status !== 'submitting') setStatus('initial');
    if (hasSubmitted) validateField(field, next);
  };

  const editConsent = (value: boolean) => {
    if (attemptedPayload.current && attemptedPayload.current !== JSON.stringify({ ...draft, consent: value })) {
      idempotencyKey.current = null;
      attemptedPayload.current = '';
    }
    setConsent(value);
    if (status !== 'submitting') setStatus('initial');
    if (hasSubmitted) validateField('consent', draft, value);
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (status === 'submitting') return;
    const nextErrors = validateDraft(draft, consent);
    setHasSubmitted(true);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setStatus('initial');
      requestAnimationFrame(() => errorSummary.current?.focus());
      return;
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setStatus('offline');
      setStatusMessage('Нет соединения с сетью. Проверьте подключение и повторите отправку вручную.');
      return;
    }

    const payloadKey = JSON.stringify({ ...draft, consent });
    if (!idempotencyKey.current) idempotencyKey.current = uuid();
    attemptedPayload.current = payloadKey;
    setStatus('submitting');
    setStatusMessage('Отправляем обращение…');
    try {
      const antiSpam = await warmChallenge();
      await submitContact(draft, idempotencyKey.current, antiSpam.token);
      setDraft(emptyDraft);
      setConsent(false);
      setErrors({});
      setHasSubmitted(false);
      idempotencyKey.current = null;
      attemptedPayload.current = '';
      challenge.current = null;
      void warmChallenge().catch(() => undefined);
      setStatus('success');
      setStatusMessage(contactSuccessCopy.responseTime);
    } catch (error) {
      if (error instanceof ContactApiError && error.code === 'CONSENT_REVISION_STALE') {
        setConsent(false);
        setErrors((current) => ({ ...current, consent: 'Редакция согласия изменилась. Отправка остановлена; подтвердите актуальную редакцию после обновления сайта.' }));
        setStatus('stale-consent');
        setStatusMessage('Сервер использует более новую редакцию согласия. Черновик сохранен в этой вкладке; напишите нам по email или повторите после обновления сайта.');
      } else if (error instanceof ContactApiError && error.code === 'RATE_LIMITED') {
        setStatus('rate-limited');
        setStatusMessage(`Слишком много попыток за короткое время. Повторите позже${error.retryAfter ? ` (рекомендованный интервал: ${error.retryAfter} сек.)` : ''} или напишите по email.`);
      } else {
        if (error instanceof ContactApiError && error.code === 'SUBMISSION_REJECTED') {
          challenge.current = null;
          void warmChallenge().catch(() => undefined);
        }
        setStatus(typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'error');
        setStatusMessage(error instanceof Error ? error.message : 'Не удалось отправить обращение. Попробуйте еще раз.');
        if (error instanceof ContactApiError && error.fields) {
          const mapped: FieldErrors = {};
          if (error.fields.name) mapped.name = error.fields.name;
          if (error.fields['contact.value'] || error.fields['contact.kind']) mapped.contact = error.fields['contact.value'] || error.fields['contact.kind'];
          if (error.fields.message) mapped.message = error.fields.message;
          if (error.fields['consent.accepted'] || error.fields['consent.revision']) mapped.consent = error.fields['consent.accepted'] || error.fields['consent.revision'];
          setErrors((current) => ({ ...current, ...mapped }));
        }
      }
    }
  };

  if (status === 'success') {
    return <ContactSuccess panelRef={successPanel} onReset={() => { setStatus('initial'); setHasSubmitted(false); }} />;
  }

  const visibleErrors = fieldOrder.filter((field) => errors[field]);
  const showRetry = ['error', 'rate-limited', 'offline'].includes(status);

  return (
    <form id={idPrefix === 'field' ? 'request' : `${idPrefix}-request`} className="form-panel" onSubmit={submit} onFocus={() => void warmChallenge().catch(() => undefined)} noValidate aria-busy={status === 'submitting'}>
      {showHeading && <div className="form-heading">
        <p className="eyebrow">Форма обращения</p>
        <h2>Расскажите о задаче</h2>
        <p>Оставьте один контакт и кратко опишите задачу. Мы не запрашиваем бюджет, адрес или вложения.</p>
      </div>}
      <div className="visually-hidden" aria-live="polite" aria-atomic="true">{statusMessage}</div>

      {hasSubmitted && visibleErrors.length > 0 && (
        <div className="error-summary" role="alert" aria-labelledby={`${idPrefix}-error-summary-title`} tabIndex={-1} ref={errorSummary}>
          <strong id={`${idPrefix}-error-summary-title`}>Проверьте поля формы:</strong>
          <ul>{visibleErrors.map((field) => <li key={field}><a href={`#${fieldTargetId(field)}`} onClick={(event) => { event.preventDefault(); focusField(field); }}>{fieldLabels[field]}: {errors[field]}</a></li>)}</ul>
        </div>
      )}

      <div className="field">
        <label htmlFor={controlId('name')}>Имя {hasSubmitted && !draft.name.trim() && <span className="required">(обязательное поле)</span>}</label>
        <input ref={initialFocusRef} id={controlId('name')} name="name" autoComplete="name" maxLength={100} value={draft.name} onChange={(event) => editDraft('name', event.target.value)} onBlur={() => { if (hasSubmitted) validateField('name'); }} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? errorId('name') : undefined} aria-errormessage={errors.name ? errorId('name') : undefined} disabled={status === 'submitting'} />
        {errors.name && <p className="field-error" id={errorId('name')}>{errors.name}</p>}
      </div>

      <div className="field">
        <label htmlFor={controlId('contact')}>Email или телефон {hasSubmitted && !draft.contact.trim() && <span className="required">(обязательное поле)</span>}</label>
        <input id={controlId('contact')} name="contact" autoComplete="email" inputMode="email" maxLength={254} value={draft.contact} onChange={(event) => editDraft('contact', event.target.value)} onBlur={() => { if (hasSubmitted) validateField('contact'); }} aria-invalid={Boolean(errors.contact)} aria-describedby={`${hintId('contact')}${errors.contact ? ` ${errorId('contact')}` : ''}`} aria-errormessage={errors.contact ? errorId('contact') : undefined} disabled={status === 'submitting'} />
        <p className="hint" id={hintId('contact')}>Например, name@company.ru или +7 900 000-00-00. Достаточно одного контакта.</p>
        {errors.contact && <p className="field-error" id={errorId('contact')}>{errors.contact}</p>}
      </div>

      <div className="field">
        <label htmlFor={controlId('message')}>Сообщение {hasSubmitted && !draft.message.trim() && <span className="required">(обязательное поле)</span>}</label>
        <textarea id={controlId('message')} name="message" rows={7} minLength={10} maxLength={4000} value={draft.message} onChange={(event) => editDraft('message', event.target.value)} onBlur={() => { if (hasSubmitted) validateField('message'); }} aria-invalid={Boolean(errors.message)} aria-describedby={`${hintId('message')}${errors.message ? ` ${errorId('message')}` : ''}`} aria-errormessage={errors.message ? errorId('message') : undefined} disabled={status === 'submitting'} />
        <p className="hint counter" id={hintId('message')}>{draft.message.length} из 4000 символов</p>
        {errors.message && <p className="field-error" id={errorId('message')}>{errors.message}</p>}
      </div>

      <div className={`consent-field${errors.consent ? ' invalid' : ''}`} id={fieldTargetId('consent')} tabIndex={-1}>
        <input type="checkbox" id={controlId('consent')} name="consent" checked={consent} onChange={(event) => { editConsent(event.target.checked); if (hasSubmitted) validateField('consent', draft, event.target.checked); }} aria-invalid={Boolean(errors.consent)} aria-describedby={`${hintId('consent')}${errors.consent ? ` ${errorId('consent')}` : ''}`} aria-errormessage={errors.consent ? errorId('consent') : undefined} disabled={status === 'submitting'} />
        <div>
          <label htmlFor={controlId('consent')}>Я даю отдельное согласие ООО «СпаркСофт» на обработку имени, одного контакта и текста обращения для ответа на него.</label>
          <p className="hint" id={hintId('consent')}>Полный текст <a href="/legal/consent" target="_blank" rel="noreferrer">согласия <span className="visually-hidden">(откроется в новой вкладке)</span></a> и <a href="/legal/privacy" target="_blank" rel="noreferrer">политика ПД <span className="visually-hidden">(откроется в новой вкладке)</span></a> доступны до отправки.</p>
          {errors.consent && <p className="field-error" id={errorId('consent')}>{errors.consent}</p>}
        </div>
      </div>

      {status !== 'initial' && (
        <div ref={statusPanel} className={`form-status ${status}`} role={status === 'error' ? 'alert' : 'status'} aria-live="polite" tabIndex={-1}>
          <strong>{status === 'submitting' ? 'Отправка' : status === 'stale-consent' ? 'Нужно новое согласие' : 'Не удалось отправить обращение'}</strong>
          <p>{statusMessage}</p>
          {showRetry && <button className="button secondary" type="button" onClick={() => void submit()} disabled={status === 'submitting'}>Повторить отправку</button>}
          {status !== 'submitting' && <p>Альтернатива: <a href="mailto:info@sparksoft.ru">info@sparksoft.ru</a>.</p>}
        </div>
      )}

      <button className="button primary submit-button" type="submit" disabled={status === 'submitting'}>{status === 'submitting' ? 'Отправляем…' : 'Отправить обращение'}</button>
      <p className="privacy-note">Черновик и ключ безопасного повтора хранятся только в памяти этой вкладки. Cookies и постоянное browser storage не используются.</p>
      <noscript><p className="form-status error">Для отправки формы нужен JavaScript. Напишите на <a href="mailto:info@sparksoft.ru">info@sparksoft.ru</a>; чтение сайта доступно без JavaScript.</p></noscript>
    </form>
  );
}
